/**
 * Player profile screen (Jonathan 2026-05-21).
 *
 * Create / modify the participant's own profile — display name + avatar.
 * Reachable from the start menu (`LauncherScene` PROFILE tile,
 * `returnTo: "launcher"`) and the pause menu (`RunnerScene` PROFILE button,
 * `returnTo: "runner"`). First run seeds a random name + avatar
 * (`ProfileService`), persisted in localStorage.
 *
 * Renders as a compact centred popup over a dimmed backdrop (not a full-screen
 * panel). Two columns: column 1 is the avatar (a 64×64 frame with ◄ / ► over
 * its lower corners and an edit pen in the top-right); column 2 stacks the name
 * (label + value + edit pen) and the simulated rating, with SAVE / BACK along
 * the bottom.
 *
 * Avatar editing has two paths:
 *  - the ◄ / ► arrows cycle a pool of 20 randomly generated "standard"
 *    avatars (`avatarKind: "generated"`);
 *  - the pen opens the Asset-Creation file picker (`FilePickerScene`,
 *    `returnTo: "profile"`); the picked photo becomes the avatar AND the
 *    player's single created asset (`avatarKind: "asset"`).
 *
 * Controller-first: WASD / arrows walk the focus ring
 * [◄, ►, ✎ avatar, ✎ name, SAVE, BACK]; J / K / Space (or gamepad A/X/Y)
 * activate the focused button. The name is edited via the reusable
 * `OnScreenKeyboard` so it works on a gamepad.
 */

import Phaser from "phaser";

import { NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";
import type { PlayerProfile } from "../types";

import { AssetService } from "../services/AssetService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { InputService } from "../services/InputService";
import { ProfileService } from "../services/ProfileService";
import { RatingService } from "../services/RatingService";
import { hashString } from "../services/seededRng";
import { OnScreenKeyboard } from "../ui/OnScreenKeyboard";
import { IconButton } from "../ui/IconButton";
import { PauseMenuButton } from "../ui/PauseMenuButton";

import { PROFILE_AVATAR_PICKED_KEY } from "./FilePickerScene";

export interface ProfileSceneInit {
  returnTo: "runner" | "launcher";
}

const AVATAR_SCALE = 4; // 16×16 → 64×64

// Compact centred popup (native render is 384×216) — not a full-screen panel.
// Everything else is positioned relative to this box so the layout stays tight.
const PANEL_W = 268;
const PANEL_H = 152;
const PANEL_X = (NATIVE_WIDTH - PANEL_W) / 2; // 58
const PANEL_Y = (NATIVE_HEIGHT - PANEL_H) / 2; // 32
const PANEL_CX = NATIVE_WIDTH / 2; // 192

const TITLE_Y = PANEL_Y + 12;
const AV_X = PANEL_X + 16;
const AV_Y = PANEL_Y + 28;
const AV_SIZE = 64;
const COL2_X = AV_X + AV_SIZE + 14;
const NAME_LABEL_Y = PANEL_Y + 34;
const NAME_VALUE_Y = PANEL_Y + 50;
const RATING_Y = PANEL_Y + 72;
const BTN_W = 96;
const BTN_GAP = 12;
const BTN_Y = PANEL_Y + PANEL_H - 28;
const HINT_Y = PANEL_Y + PANEL_H - 8;
const AVATAR_POOL_SIZE = 20;
/** Avatar edit pen — larger than the default chip so it reads as a pencil. */
const AVATAR_PEN_SIZE = 22;

interface FocusTarget {
  activate: () => void;
  setFocused: (focused: boolean) => void;
}

export class ProfileScene extends Phaser.Scene {
  private returnTo: ProfileSceneInit["returnTo"] = "launcher";
  private input2!: InputService;
  private identityService!: AuthorIdentityService;
  private telemetry!: {
    emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void;
  };

  private working!: PlayerProfile;
  private avatarImg?: Phaser.GameObjects.Image;
  private avatarCorner?: Phaser.GameObjects.Rectangle;
  private penBtn!: IconButton;
  private leftArrow!: IconButton;
  private rightArrow!: IconButton;
  private namePenBtn!: IconButton;
  private nameValue!: Phaser.GameObjects.Text;
  private ratingValue!: Phaser.GameObjects.Text;

  /** Pool of 20 "standard" generated avatar seeds the arrows cycle through. */
  private avatarPool: number[] = [];
  private avatarPoolIdx = 0;

  private focusTargets: FocusTarget[] = [];
  private focusIdx = 0;
  private prevMoveY = 0;
  private prevMoveX = 0;
  private keyboard?: OnScreenKeyboard;
  private openedAtMs = 0;

  constructor() {
    super({ key: "ProfileScene" });
  }

  init(data: ProfileSceneInit): void {
    this.returnTo = data.returnTo ?? "launcher";
  }

  create(): void {
    this.input2 = new InputService(this);
    this.openedAtMs = this.time.now;

    this.identityService =
      (this.registry.get("authorIdentityService") as AuthorIdentityService | undefined) ??
      new AuthorIdentityService();
    if (!this.registry.get("authorIdentityService")) {
      this.registry.set("authorIdentityService", this.identityService);
    }
    this.telemetry =
      (this.registry.get("telemetryService") as ProfileScene["telemetry"] | undefined) ??
      { emit: () => undefined };

    const stored = ProfileService.get();
    this.working = { name: stored.name, avatarSeed: stored.avatarSeed, avatarKind: stored.avatarKind };

    this.buildAvatarPool();
    this.buildLayout();

    // Re-applied each time we resume from the file picker (custom avatar).
    this.events.on(Phaser.Scenes.Events.RESUME, this.handleResume, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.RESUME, this.handleResume, this);
    });

    this.telemetry.emit(1, "profile.open", { source: this.returnTo });
  }

  override update(): void {
    // While the on-screen keyboard owns input, the scene stays out of the way.
    if (this.keyboard?.isOpen()) return;
    const input = this.input2.read();

    if (input.pausePressed) {
      this.close(false);
      return;
    }

    const dy = input.moveY > 0.5 ? 1 : input.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevMoveY) {
      this.prevMoveY = dy;
      if (dy !== 0) this.moveFocus(dy);
    }

    // Left/right move focus too — WASD / arrows all walk the button ring.
    // The avatar arrows are themselves focusable buttons (activated with the
    // confirm keys), so ←/→ no longer cycle avatars directly.
    const dx = input.moveX > 0.5 ? 1 : input.moveX < -0.5 ? -1 : 0;
    if (dx !== this.prevMoveX) {
      this.prevMoveX = dx;
      if (dx !== 0) this.moveFocus(dx);
    }

    if (input.interactPressed || input.attackPressed || input.jumpPressed) {
      this.focusTargets[this.focusIdx]?.activate();
    }
  }

  // -- layout ---------------------------------------------------------------

  private buildLayout(): void {
    this.focusTargets = [];
    this.focusIdx = 0;

    // No backdrop — the area around the popup stays transparent so the scene
    // behind (the paused runner / launcher) shows through undimmed.

    // Compact centred panel with a cyan UGC corner-mark.
    this.add
      .rectangle(PANEL_CX, NATIVE_HEIGHT / 2, PANEL_W, PANEL_H, PALETTE.authorPanelBg, 1)
      .setStrokeStyle(2, PALETTE.authorPanelEdge);
    this.add.rectangle(PANEL_X + 2, PANEL_Y + 2, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    this.add
      .text(PANEL_CX, TITLE_Y, t("profile.title"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.hex(PALETTE.authorPanelEdge),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    // -- Column 1: avatar with edit pen + cycle arrows ----------------------
    this.add
      .rectangle(AV_X, AV_Y, AV_SIZE, AV_SIZE, PALETTE.inventoryEmpty, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.redrawAvatar();

    this.leftArrow = new IconButton(this, AV_X + 8, AV_Y + AV_SIZE - 8, "◄", () => {
      this.focusIdx = 0;
      this.repaintFocus();
      this.cycleAvatar(-1);
    });
    this.rightArrow = new IconButton(this, AV_X + AV_SIZE - 8, AV_Y + AV_SIZE - 8, "►", () => {
      this.focusIdx = 1;
      this.repaintFocus();
      this.cycleAvatar(1);
    });
    this.penBtn = new IconButton(
      this,
      AV_X + AV_SIZE - 6,
      AV_Y + 6,
      "✎",
      () => {
        this.focusIdx = 2;
        this.repaintFocus();
        this.openCustomAvatar();
      },
      AVATAR_PEN_SIZE,
    );

    // -- Column 2: name, rating, actions ------------------------------------
    this.add
      .text(COL2_X, NAME_LABEL_Y, t("profile.name"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.hex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.nameValue = this.add
      .text(COL2_X, NAME_VALUE_Y, this.working.name, {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.hex(PALETTE.pauseButtonText),
        fontStyle: "bold",
      })
      .setOrigin(0, 0.5);
    this.namePenBtn = new IconButton(this, COL2_X, NAME_VALUE_Y, "✎", () => {
      this.focusIdx = 3;
      this.repaintFocus();
      this.editName();
    });
    this.positionNamePen();

    // Rating line — simulated "from other players" reputation (Review
    // System). Derived from the name so it stays stable while the name does.
    this.ratingValue = this.add
      .text(COL2_X, RATING_Y, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.hex(PALETTE.authorPanelEdge),
      })
      .setOrigin(0, 0.5);
    this.updateRatingLine();

    const saveBtn = new PauseMenuButton(this, PANEL_CX - (BTN_W + BTN_GAP) / 2, BTN_Y, t("profile.save"), () => this.close(true), BTN_W);
    const backBtn = new PauseMenuButton(this, PANEL_CX + (BTN_W + BTN_GAP) / 2, BTN_Y, t("profile.cancel"), () => this.close(false), BTN_W);

    // Focus ring (WASD / arrows walk it; J / K / Space confirm):
    // [◄ prev avatar, ► next avatar, ✎ custom avatar, ✎ name, SAVE, BACK].
    this.focusTargets = [
      { activate: () => this.cycleAvatar(-1), setFocused: (f) => this.leftArrow.setFocused(f) },
      { activate: () => this.cycleAvatar(1), setFocused: (f) => this.rightArrow.setFocused(f) },
      { activate: () => this.openCustomAvatar(), setFocused: (f) => this.penBtn.setFocused(f) },
      { activate: () => this.editName(), setFocused: (f) => this.namePenBtn.setFocused(f) },
      { activate: () => this.close(true), setFocused: (f) => saveBtn.setFocused(f) },
      { activate: () => this.close(false), setFocused: (f) => backBtn.setFocused(f) },
    ];
    this.repaintFocus();

    this.add
      .text(PANEL_CX, HINT_Y, t("profile.hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.hex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
  }

  /** Position the name edit pen immediately after the (variable-width) name. */
  private positionNamePen(): void {
    this.namePenBtn.container.setPosition(COL2_X + this.nameValue.width + 10, NAME_VALUE_Y);
  }

  private buildAvatarPool(): void {
    const pool: number[] = [];
    if (this.working.avatarKind === "generated") pool.push(this.working.avatarSeed >>> 0);
    while (pool.length < AVATAR_POOL_SIZE) {
      const seed = ProfileService.randomAvatarSeed();
      if (!pool.includes(seed)) pool.push(seed);
    }
    this.avatarPool = pool;
    this.avatarPoolIdx = 0;
  }

  private redrawAvatar(): void {
    this.avatarImg?.destroy();
    this.avatarCorner?.destroy();
    this.avatarCorner = undefined;

    if (this.working.avatarKind === "asset") {
      const uploaded = AssetService.getUploaded();
      if (uploaded) {
        const key = AssetService.uploadedThumbnailKey(this, uploaded, AV_SIZE, AV_SIZE);
        this.avatarImg = this.add.image(AV_X, AV_Y, key).setOrigin(0, 0).setDepth(2);
        this.avatarCorner = this.add.rectangle(AV_X, AV_Y, 4, 4, UGC_CYAN, 1).setOrigin(0, 0).setDepth(3);
        return;
      }
      // Asked for a custom avatar but nothing uploaded — fall back to generated.
      this.working.avatarKind = "generated";
    }

    const identity = this.identityService.forSeed(this.working.avatarSeed);
    this.avatarImg = this.identityService.drawAvatar(this, AV_X, AV_Y, identity, AVATAR_SCALE).setDepth(2);
  }

  private updateRatingLine(): void {
    const agg = RatingService.simulatedAggregate(hashString(this.working.name));
    this.ratingValue.setText(
      `${t("profile.rating")}: ${t("profile.rating.value", { avg: agg.fun.toFixed(1), count: agg.count })}`,
    );
  }

  // -- actions --------------------------------------------------------------

  private editName(): void {
    if (this.keyboard?.isOpen()) return;
    this.keyboard = new OnScreenKeyboard(this, {
      title: t("profile.kb.title"),
      initial: this.working.name,
      maxLen: 16,
      onSubmit: (value) => {
        this.keyboard = undefined;
        const name = value.trim();
        if (name.length > 0) {
          this.working.name = name;
          this.nameValue.setText(name);
          this.positionNamePen();
          this.updateRatingLine();
          this.telemetry.emit(1, "profile.name.edit", { len: name.length });
        }
      },
      onCancel: () => {
        this.keyboard = undefined;
      },
    });
  }

  /** Step the generated-avatar pool by ±1 (wrap-around). */
  private cycleAvatar(dir: number): void {
    if (this.avatarPool.length === 0) return;
    this.avatarPoolIdx = (this.avatarPoolIdx + dir + this.avatarPool.length) % this.avatarPool.length;
    this.working.avatarSeed = this.avatarPool[this.avatarPoolIdx]!;
    this.working.avatarKind = "generated";
    this.redrawAvatar();
    this.telemetry.emit(1, "profile.avatar.cycle", { dir, seed: this.working.avatarSeed });
  }

  /** Open the asset file picker to assign a custom photo as the avatar. */
  private openCustomAvatar(): void {
    if (this.keyboard?.isOpen()) return;
    this.telemetry.emit(1, "profile.avatar.custom.open");
    this.scene.pause();
    this.scene.launch("FilePickerScene", { returnTo: "profile" });
  }

  /** Returning from the picker: adopt the picked asset as the avatar. */
  private handleResume(): void {
    this.prevMoveX = 0;
    this.prevMoveY = 0;
    if (this.registry.get(PROFILE_AVATAR_PICKED_KEY)) {
      this.registry.set(PROFILE_AVATAR_PICKED_KEY, false);
      this.working.avatarKind = "asset";
      this.redrawAvatar();
      this.telemetry.emit(1, "profile.avatar.custom.set");
    }
  }

  private moveFocus(dir: number): void {
    this.focusIdx = Phaser.Math.Clamp(this.focusIdx + dir, 0, this.focusTargets.length - 1);
    this.repaintFocus();
  }

  private repaintFocus(): void {
    this.focusTargets.forEach((target, i) => target.setFocused(i === this.focusIdx));
  }

  private close(save: boolean): void {
    if (save) {
      ProfileService.save(this.working);
      this.telemetry.emit(1, "profile.save", { nameLen: this.working.name.length, avatar: this.working.avatarKind });
    } else {
      this.telemetry.emit(1, "profile.cancel", { dwellMs: Math.round(this.time.now - this.openedAtMs) });
    }
    this.keyboard?.destroy();
    this.keyboard = undefined;
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else {
      this.scene.start("launcher");
    }
  }

  private hex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }
}
